import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CalendarDays } from "lucide-react";
import { Reveal } from "@/components/site/Widgets";
import { useMeta } from "@/pages/SeoPages";
import { api, track, WHATSAPP_URL } from "@/lib/api";

export const CATEGORIES = ["All", "Romania", "Europe", "Bachelor's", "Master's", "Scholarships & Funding", "Admissions", "Visa Guidance", "Student Life", "Careers"];

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

function PostCard({ post, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
      whileHover={{ y: -6 }}
      data-testid={`insight-card-${post.slug}`}
      className="group bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 transition-all duration-300 flex flex-col"
    >
      <Link to={`/insights/${post.slug}`} className="block">
        <div className="relative h-48 overflow-hidden bg-[#0B192C]">
          {post.cover_image ? (
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          ) : (
            <div className="w-full h-full mesh-gradient bg-[#0B192C] flex items-center justify-center">
              <span className="font-editorial italic text-2xl text-[#F59E0B]">Insights</span>
            </div>
          )}
          <span className="absolute top-4 left-4 bg-white/90 backdrop-blur text-[#0B192C] text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
            {post.category}
          </span>
        </div>
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-xs font-data text-slate-400 flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5" /> {fmtDate(post.published_at)}
        </p>
        <Link to={`/insights/${post.slug}`}>
          <h3 className="mt-2.5 text-lg font-bold text-[#0B192C] leading-snug group-hover:text-[#1E40AF] transition-colors">{post.title}</h3>
        </Link>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{post.excerpt}</p>
        <Link to={`/insights/${post.slug}`} data-testid={`insight-read-${post.slug}`}
          className="mt-4 text-[13px] font-bold text-[#1E40AF] flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
          READ ARTICLE <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  );
}

export function InsightsPage() {
  useMeta("Study Abroad Insights | Romania & Europe Guides | Harwell Global Education",
    "Guides and insights on studying in Romania and Europe — costs, visas, admissions, student life and careers, from Harwell Global Education counsellors.");
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/content/posts").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const filtered = posts.filter((p) => cat === "All" || p.category === cat);

  return (
    <div data-testid="insights-page">
      <section className="bg-[#070E1E] pt-40 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-data uppercase tracking-[0.25em] text-[#38BDF8] font-semibold mb-5">Resource Centre</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05]">
            STUDY ABROAD <span className="font-editorial italic font-semibold text-[#F59E0B]">INSIGHTS.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl">
            Honest guides on studying in Romania and Europe — costs, visas, admissions and student life. No hype, just clarity.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2.5 mb-12" data-testid="insights-category-filters">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                  cat === c ? "bg-[#0B192C] text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-[#1E40AF] hover:text-[#1E40AF]"
                }`}>
                {c}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => <PostCard key={p.id} post={p} i={i} />)}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-slate-400 py-16" data-testid="insights-empty">No articles in this category yet — check back soon.</p>
          )}
        </div>
      </section>
    </div>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get("/content/posts").then((r) => {
      const found = r.data.find((p) => p.slug === slug);
      if (found) setPost(found);
      else setMissing(true);
    }).catch(() => setMissing(true));
  }, [slug]);

  useMeta(
    post ? `${post.title} | Harwell Global Education` : "Study Abroad Insights | Harwell Global Education",
    post?.excerpt || "Study abroad guides from Harwell Global Education."
  );

  if (missing) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="article-not-found">
        <h1 className="text-2xl font-extrabold text-[#0B192C]">Article not found</h1>
        <Link to="/insights" className="mt-4 inline-block text-sm font-bold text-[#1E40AF]">Back to Insights</Link>
      </div>
    );
  }
  if (!post) return <div className="pt-40 pb-32 text-center text-slate-400">Loading...</div>;

  const blocks = post.content.split("\n\n");

  return (
    <div data-testid="article-page">
      <section className="relative bg-[#070E1E] pt-40 pb-20 overflow-hidden">
        {post.cover_image && (
          <>
            <img src={post.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070E1E] via-[#070E1E]/70 to-[#070E1E]/40" />
          </>
        )}
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/insights" data-testid="article-back-link" className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-300 hover:text-white mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> ALL INSIGHTS
          </Link>
          <span className="block w-fit bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-5">
            {post.category}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-[1.08]">{post.title}</h1>
          <p className="mt-5 text-xs font-data text-slate-400 flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" /> {fmtDate(post.published_at)} • Harwell Global Education
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {blocks.map((b, i) =>
            b.startsWith("## ") ? (
              <h2 key={i} className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight mt-10 mb-4">{b.slice(3)}</h2>
            ) : (
              <p key={i} className="text-slate-600 leading-relaxed mb-5">{b}</p>
            )
          )}

          <Reveal className="mt-14 bg-[#0B192C] rounded-2xl p-8 sm:p-10 text-center border border-[#1E355B]">
            <h3 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">Have questions about your own profile?</h3>
            <p className="text-slate-400 text-sm mt-2">Get a free, honest assessment from a Harwell counsellor.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button onClick={() => navigate("/#contact")} data-testid="article-cta-counselling"
                className="px-7 py-3.5 rounded-full text-sm font-bold text-[#0B192C] bg-white hover:bg-slate-100 active:scale-[0.98] transition-all">
                GET FREE COUNSELLING
              </button>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { source: "article" })}
                data-testid="article-cta-whatsapp"
                className="px-7 py-3.5 rounded-full text-sm font-bold text-white bg-[#059669] hover:bg-[#047857] active:scale-[0.98] transition-all">
                WHATSAPP US
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
